import React from "react";
import { Link } from "react-router-dom";
import { ContentGrid, SeoHero, TextSection } from "./SeoPageBlocks";

import { useLandingRegistration } from "../../lib/useLandingRegistration.js";

const howItWorksItems = [
  {
    title: "Lag och grupper kan sälja",
    text: "Föreningens lag eller grupper kan använda SalesPartner för att samla in pengar. Varje grupp behöver ett tydligt ansvar.",
  },
  {
    title: "Gruppledaren håller ihop försäljningen",
    text: "En gruppledare registrerar gruppen, slutför onboarding och ser till att säljarna kommer igång.",
  },
  {
    title: "Säljarna får egna länkar",
    text: "Varje säljare får en personlig säljlänk och QR-kod. Beställningar via länken kopplas till rätt säljare och rätt grupp.",
  },
];

const processItems = [
  {
    title: "Registrering och godkännande",
    text: "Gruppledaren registrerar gruppen och lämnar uppgifter om den planerade försäljningsperioden. Registreringen är en ansökan och måste godkännas innan gruppen går vidare.",
  },
  {
    title: "Onboarding och inbjudan",
    text: "Efter godkännande slutför gruppledaren onboarding och bjuder in säljarna. Varje säljare får en personlig säljlänk och QR-kod.",
  },
  {
    title: "Beställning och leverans",
    text: "Kunden beställer via säljlänken. De betalningsalternativ som är tillgängliga visas i kassan. Beställningen levereras antingen direkt till kunden eller gemensamt till gruppen.",
  },
  {
    title: "Avslut och utbetalning",
    text: "När försäljningsperioden är slut avslutas kampanjen. Efter att beställningarna har levererats och väntetiden har passerat kan gruppen begära utbetalning.",
  },
];

const responsibilityItems = [
  {
    title: "Registrering och onboarding",
    text: "Gruppledaren skickar in ansökan, väntar på godkännande och kompletterar därefter de uppgifter som behövs för gruppens upplägg.",
  },
  {
    title: "Bjuda in säljare och hålla ihop gruppen",
    text: "När gruppen är godkänd bjuder gruppledaren in säljarna så att varje säljare kan använda sin personliga länk och QR-kod.",
  },
  {
    title: "Utdelning vid gruppleverans",
    text: "Väljer gruppen gruppleverans skickas beställningarna till gruppens adress. Gruppledaren tar emot leveransen och ansvarar för utdelningen.",
  },
];

const startLinks = [
  { to: "/register-group", label: "Registrera ett lag" },
  { to: "/contact", label: "Kontakta oss om flera lag ska samordnas" },
  { to: "/sa-fungerar-salespartner", label: "Se hur hela flödet fungerar steg för steg" },
  { to: "/vanliga-fragor", label: "Läs vanliga frågor" },
];

function TextLink({ to, children }) {
  return (
    <Link
      to={to}
      className="font-medium text-amber-800 underline decoration-amber-300 underline-offset-2 hover:text-amber-950 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-400 focus-visible:ring-offset-2"
    >
      {children}
    </Link>
  );
}

export default function TjanaPengarForeningenPage() {
  const registration = useLandingRegistration("footer-cta");
  return (
    <div className="bg-slate-50">
      <SeoHero
        eyebrow="För föreningar"
        title="Tjäna pengar till föreningen med tydliga grupper"
        description="Föreningens lag och grupper kan samla in pengar med egna gruppledare, personliga säljlänkar och ett digitalt flöde från beställning till utbetalning."
        primaryCtaLabel="Registrera ett lag"
        primaryCtaTo="/register-group"
        secondaryCtaLabel="Se hur hela flödet fungerar steg för steg"
        secondaryCtaTo="/sa-fungerar-salespartner"
      />

      <TextSection
        eyebrow="Digital föreningsförsäljning"
        title="Så fungerar föreningsförsäljning med SalesPartner"
        intro="Föreningen samlar in pengar genom att lagen eller grupperna driver sina egna försäljningar."
      >
        <ContentGrid items={howItWorksItems} />
      </TextSection>

      <TextSection
        eyebrow="Två vägar in"
        title="Ett upplägg för föreningens lag och grupper"
        tone="muted"
      >
        <p>
          Ett enskilt lag kan registreras via det publika formuläret, som stöder idrottslag och
          skolklass. Gruppledaren ansvarar då för just den gruppen.
        </p>
        <p>
          Ska flera lag samordnas i ett större upplägg är det ett nästa steg, inte ett krav för att
          ett lag ska kunna börja.
        </p>
      </TextSection>

      <TextSection
        eyebrow="Från ansökan till utbetalning"
        title="Så går försäljningen till"
      >
        <ContentGrid items={processItems} columns={2} />
        <p>
          Kunden ser de betalningsalternativ som är tillgängliga i kassan. Säljarna behöver inte
          hantera kontanter. Betalnings- och leveransupplägg kan variera.{" "}
          <TextLink to="/leveransvillkor">Läs aktuella leveransvillkor</TextLink>
          {" · "}
          <TextLink to="/products">Se aktuella produkter i sortimentet</TextLink>
        </p>
      </TextSection>

      <TextSection
        eyebrow="Provision och utbetalning"
        title="Hur förtjänsten hanteras"
        tone="muted"
      >
        <p>
          Provisionen är produkt- och variantbunden och kan variera. Gruppens verkliga förtjänst
          räknas på den faktiska provisionen för det som säljs.
        </p>
        <p>
          För planering finns en kalkylator som använder 74 kr i genomsnittlig provision per såld
          produkt som planeringsvärde. Det är inte en garanti eller fast provision.
        </p>
        <p>
          Efter att alla beställningar har levererats och väntetiden på 14 dagar har passerat kan
          gruppen begära utbetalning. Returer och återbetalningar kan påverka slutbeloppet.
        </p>
        <p>
          <TextLink to="/provision-och-fortjanst">Räkna på ett tydligt upplägg</TextLink>
        </p>
      </TextSection>

      <TextSection
        eyebrow="Tydligt ansvar"
        title="Vad gruppledaren ansvarar för"
      >
        <ContentGrid items={responsibilityItems} />
      </TextSection>

      <TextSection
        eyebrow="Flera lag"
        title="När flera lag ska delta"
        tone="muted"
      >
        <p>
          Ett lag eller en grupp kan köra sitt eget upplägg. Ska flera lag samordnas hjälper
          SalesPartner till att hitta rätt struktur innan start.
        </p>
        <p>
          <TextLink to="/contact">Kontakta oss om flera lag ska samordnas</TextLink>
        </p>
      </TextSection>

      <TextSection
        eyebrow="Nästa steg"
        title="Kom igång"
        intro="Börja med ett lag eller en grupp, eller hör av er om flera lag ska samordnas."
      >
        <div className="grid sm:grid-cols-2 gap-4">
          {startLinks.map((link) => (
            <Link
              key={link.to}
              to={link.to === "/register-group" ? registration.to : link.to}
              onClick={link.to === "/register-group" ? registration.onClick : undefined}
              className="rounded-2xl border border-slate-200 bg-slate-50 p-5 font-medium text-slate-800 transition hover:border-amber-300 hover:bg-white hover:shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-400 focus-visible:ring-offset-2"
            >
              {link.label}
            </Link>
          ))}
        </div>
      </TextSection>
    </div>
  );
}
